// AGENT-A6: The grouped filter strip that lives ABOVE the metrics table.
//
// HSQ-tweak (2026-05-27): the slicers are organised into labelled clusters
// (Date & Mode / Customer / Order Channel / Product Attributes / Scope) inside
// subtle fieldset-style boxes so the bar reads as intentional groups. The
// Vendor + Parent Product multiselects live in the Scope group; the Web slicer
// is intentionally omitted (it matches 0 rows on the IA-based pivot).
//
// Layout:
//   Row 1: DATE RANGE (left) | MODE toggles + Reset (right-aligned)
//   Row 2: grouped boolean/tri slicers (one labelled box per cluster)
//
// All controls live inside a single .dash-card so no chip can "float loose".

import { useCallback, useMemo } from "react";
import { BooleanToggle } from "./BooleanToggle";
import { TriToggle } from "./TriToggle";
import { TriBooleanToggle } from "./TriBooleanToggle";
import { ModeToggles } from "./ModeToggles";
// AGENT-A4: DATE RANGE segmented control (FY vs Dynamic).
import { DateRangeToggle } from "./DateRangeToggle";
// Scope multiselects — restored 2026-05-27 after confirming both narrow the
// report (Parent Product 376→4, Vendor 376→194). The Web slicer stays out:
// it filters Product Table.WEB, which matches 0 rows on the IA-based pivot.
import { VendorMultiselect } from "./VendorMultiselect";
import { ParentProductMultiselect } from "./ParentProductMultiselect";
// HSQ-tweak: period selectors now live atop the filter panel (connected look).
import { ViewSelectors } from "../views/ViewSelectors";
import { useFilterStore } from "../state/filterStore";
import type { DateRange, NewOrExisting } from "../types";
import "./FilterBar.css";

/* The boolean filter keys + labels. Keys mirror /api/dashboard/filters →
 * booleans[].key and the _BOOLEAN_KEYS list in
 * webapp/backend/app/routers/dashboard.py.
 *
 * NEW/EXISTING and Online Order are tri-state and rendered separately. */
interface BooleanSpec {
  key: string;
  header: string;
  labels: [string, string];
}

const BOOLEAN_BY_KEY: Record<string, BooleanSpec> = {
  discontinued: { key: "discontinued", header: "Discontinued", labels: ["Yes", "No"] },
  // NOTE: the `web` slicer was removed — it filters Product Table.WEB (a
  // product flag), which doesn't reliably project onto the Item-Analysis
  // pivot, so "Web = Yes" matched 0 rows. The "Online Order" tri-toggle
  // already covers the order-channel question.
  custom: { key: "custom", header: "Custom", labels: ["Yes", "No"] },
  new12mo: { key: "new12mo", header: "New <12mo", labels: ["Yes", "No"] },
  cleanroom: { key: "cleanroom", header: "Cleanroom", labels: ["Yes", "No"] },
  stocked: { key: "stocked", header: "Stocked", labels: ["Yes", "No"] },
  le: { key: "le", header: "LE", labels: ["Yes", "No"] },
};

// All boolean keys (used by the value-map memo + active-count below).
const BOOLEAN_SPECS: BooleanSpec[] = Object.values(BOOLEAN_BY_KEY);

// Slicer clusters rendered as labelled groups. "Product Attributes" carries
// the bulk of the booleans; Customer + Order Channel pull out the two scope
// dimensions users reach for first.
const PRODUCT_ATTR_KEYS = [
  "cleanroom",
  "custom",
  "stocked",
  "le",
  "new12mo",
  "discontinued",
] as const;

const NEW_OR_EXISTING_OPTIONS: [
  { value: NewOrExisting; label: string },
  { value: NewOrExisting; label: string },
  { value: NewOrExisting; label: string },
] = [
  { value: "existing", label: "Existing" },
  { value: "new", label: "New" },
  { value: "both", label: "Both" },
];

export function FilterBar(): JSX.Element {
  const newOrExisting = useFilterStore((s) => s.newOrExisting);
  const setNewOrExisting = useFilterStore((s) => s.setNewOrExisting);
  const booleans = useFilterStore((s) => s.booleans);
  const setBoolean = useFilterStore((s) => s.setBoolean);
  // 1Y-online-tri: dedicated tri-state value for the online filter.
  const online = useFilterStore((s) => s.online);
  const setOnline = useFilterStore((s) => s.setOnline);
  // AGENT-A4: DATE RANGE segmented control. Drives `dateRange.kind`. We
  // preserve the existing `years` list across kind flips so flipping to
  // Dynamic and back doesn't blow away the user's FY selection.
  const dateRange = useFilterStore((s) => s.dateRange);
  const setDateRange = useFilterStore((s) => s.setDateRange);
  const categories = useFilterStore((s) => s.categories);
  const parentProducts = useFilterStore((s) => s.parentProducts);
  const partNumbers = useFilterStore((s) => s.partNumbers);
  const vendors = useFilterStore((s) => s.vendors);
  const mode = useFilterStore((s) => s.mode);
  const resetFilters = useFilterStore((s) => s.reset);
  // Count any non-default filter as "active". `mode` defaults to "orders";
  // flipping to "quotes" is a real filter change so it counts here too.
  const activeFilterCount =
    categories.size +
    parentProducts.size +
    partNumbers.size +
    vendors.size +
    Object.keys(booleans).length +
    (newOrExisting !== "both" ? 1 : 0) +
    (online !== "both" ? 1 : 0) +
    (mode !== "orders" ? 1 : 0) +
    (dateRange.kind === "dynamic" || dateRange.years.length > 0 ? 1 : 0);
  const onDateRangeKindChange = useCallback(
    (kind: "fy" | "dynamic") => {
      const next: DateRange = { ...dateRange, kind, years: [...dateRange.years] };
      setDateRange(next);
    },
    [dateRange, setDateRange],
  );

  /* Three-valued boolean adapter: A5's store stores boolean only
   * (true/false), and "either" is represented by absence from the
   * booleans map. We translate undefined → null at the field boundary
   * so BooleanToggle's value contract (true | false | null) holds. */
  const onBooleanChange = useCallback(
    (key: string) => (v: boolean | null) => {
      if (v === null) {
        // Remove the key from the booleans map. A5's setBoolean only
        // writes true/false, so we use Zustand's setState escape hatch
        // to actually delete. We do NOT modify A5's setBoolean signature.
        useFilterStore.setState((s) => {
          const next = { ...s.booleans };
          delete next[key];
          return { booleans: next };
        });
        return;
      }
      setBoolean(key, v);
    },
    [setBoolean],
  );

  const booleanValues = useMemo<Record<string, boolean | null>>(() => {
    const map: Record<string, boolean | null> = {};
    for (const spec of BOOLEAN_SPECS) {
      const v = booleans[spec.key];
      map[spec.key] = v === undefined ? null : v;
    }
    return map;
  }, [booleans]);

  return (
    <section className="dash-card dash-filterbar" aria-label="Filter bar">
      {/* HSQ-tweak (2026-05-27): the period selectors (Counts / Quantity) are
          now connected to this panel as a top strip, so the period grouping
          and the slicers read as one cohesive control surface. */}
      <ViewSelectors />

      <div className="dash-filterbar__divider" aria-hidden />

      {/* Slicers — Date & Mode, Customer, Order Channel, Product Attributes —
          each a labelled group, flowing left-to-right to fill the width. The
          Reset / active-count actions pin to the top-right. */}
      <div className="dash-filterbar__row dash-filterbar__row--groups">
        <fieldset className="dash-filter-group">
          <legend className="dash-filter-group__legend">Date &amp; Mode</legend>
          <div className="dash-filter-group__body">
            {/* AGENT-A4: DATE RANGE — FY uses the existing FY-list behaviour;
                Dynamic flips `dateRange.kind` so `toServerShape()` omits the
                date filter and the backend's intrinsic page filters take
                over (or the From/To inputs ModeToggles renders). */}
            <DateRangeToggle
              header="Date Range"
              value={dateRange.kind}
              onChange={onDateRangeKindChange}
            />
            <ModeToggles />
          </div>
        </fieldset>

        <fieldset className="dash-filter-group">
          <legend className="dash-filter-group__legend">Customer</legend>
          <div className="dash-filter-group__body">
            <TriToggle
              header="New / Existing"
              value={newOrExisting}
              onChange={setNewOrExisting}
              options={NEW_OR_EXISTING_OPTIONS}
            />
          </div>
        </fieldset>

        <fieldset className="dash-filter-group">
          <legend className="dash-filter-group__legend">Order Channel</legend>
          <div className="dash-filter-group__body">
            {/* 1Y-online-tri: tri-state online filter; default "both" leaves
                it inactive until the user picks. */}
            <TriBooleanToggle
              header="Online Order"
              value={online}
              onChange={setOnline}
            />
          </div>
        </fieldset>

        <fieldset className="dash-filter-group">
          <legend className="dash-filter-group__legend">Product Attributes</legend>
          <div className="dash-filter-group__body">
            {PRODUCT_ATTR_KEYS.map((key) => {
              const spec = BOOLEAN_BY_KEY[key];
              return (
                <BooleanToggle
                  key={spec.key}
                  header={spec.header}
                  value={booleanValues[spec.key]}
                  onChange={onBooleanChange(spec.key)}
                  labels={spec.labels}
                />
              );
            })}
          </div>
        </fieldset>

        <fieldset className="dash-filter-group">
          <legend className="dash-filter-group__legend">Scope</legend>
          <div className="dash-filter-group__body">
            <VendorMultiselect />
            <ParentProductMultiselect />
          </div>
        </fieldset>

        <div className="dash-filterbar__actions">
          <span className="dash-small dash-filterbar__count">
            {activeFilterCount} filters active
          </span>
          <button
            type="button"
            className="dash-button"
            onClick={() => resetFilters()}
          >
            Reset
          </button>
        </div>
      </div>
    </section>
  );
}

export default FilterBar;
